import React, { useEffect, useMemo, useState } from "react";
import "./OfferingGroupView.less";
import { Col, Collapse, Divider, Form, Row } from "antd";
import { Link, useParams } from "react-router-dom";
import { LeftOutlined } from "@ant-design/icons";
import { ReactComponent as VersionsHiddenIcon } from "../../../../img/versions_icons/versionsHidden_icon.svg";
import { ReactComponent as VersionsShownIcon } from "../../../../img/versions_icons/versionsShown_icon.svg";
import { ReactComponent as DotsIcon } from "../../../../img/3dots.svg";
import OfferingGroupParams from "../../features/OfferingGroupParams/OfferingGroupParams";
import { IChangingMode, IChangingRecordKeyNumber } from "../../../../lib/types/IViewMode";
import { ILoadByWithPromise } from "../../../../lib/types";
import { useAppDispatch, useAppSelector } from "../../../..";
import {
  fetchOfferingGroupId,
  fetchOfferingGroupsPolicy,
  fetchOfferingGroupsVersions,
  offeringGroupsActions,
  updateOfferingGroup,
  updateOfferingGroupCloseDate,
} from "../../store/slice/offeringGroupsSlice";
import {
  IOfferingGroup,
  IOfferingGroupCreate,
  IOfferingGroupForm,
} from "../../types/IOfferingGroup";
import {
  checkIsLoading,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
} from "../../../../lib/utils";
import OfferingGroupTreeTable from "../../features/OfferingGroupTreeTable/OfferingGroupTreeTable";
import OfferingGroupPolicyTable from "../../features/OfferingGroupPolicyTable/OfferingGroupPolicyTable";
import HistoryAndPropertyModal from "../../../../components/HistoryAndPropertyModal/HistoryAndPropertyModal";

const OfferingGroupView = () => {
  const dispatch = useAppDispatch();

  const { offeringGroupId } = useParams<{ offeringGroupId: string }>();

  const {
    offeringGroup,
    offeringGroupLoading,
    offeringGroupsVersions,
    offeringGroupsVersionsLoading,
    offeringGroupsPolicy,
    offeringGroupsPolicyLoading,
  } = useAppSelector((state) => state.offeringGroups);

  const [offeringGroupParamsForm] = Form.useForm<IOfferingGroupForm>();

  // Загрузка группы по входу
  useEffect(() => {
    if (offeringGroupId) {
      dispatch(fetchOfferingGroupId({ offeringGroupId: Number(offeringGroupId) }));
      dispatch(fetchOfferingGroupsPolicy(Number(offeringGroupId)));
    }
  }, [offeringGroupId]);

  const [activeOfferingGroupVersion, setActiveOfferingGroupVersion] = useState<IOfferingGroup>();

  // По дефолту activePolicyVersion === offeringGroup
  useEffect(() => {
    if (offeringGroup) {
      setActiveOfferingGroupVersion(offeringGroup);
    }
  }, [offeringGroup]);

  useEffect(() => {
    return () => {
      dispatch(offeringGroupsActions.cleanOfferingGroup());
    };
  }, []);

  const loadOfferingGroupVersions: ILoadByWithPromise = (id) =>
    dispatch(fetchOfferingGroupsVersions(id));

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  const showHistoryModal = () => {
    setHistoryModalVisible(true);
    setObjectId(activeOfferingGroupVersion?.id || null);
  };

  const [isVersionsOpen, setIsVersionsOpen] = useState<boolean>(false);
  const [isCollapseOpen, setIsCollapseOpen] = useState<boolean>(true);

  const toggleCollapse = () => {
    setIsCollapseOpen((prev) => !prev);
  };

  const [isCollapsePolicyOpen, setIsCollapsePolicyOpen] = useState<boolean>(true);

  const toggleCollapsePolicy = () => {
    setIsCollapsePolicyOpen((prev) => !prev);
  };

  // Флаг редактируемой версии [индекс в массиве версий, mode]
  const [childChangingMode, setChildChangingMode] = useState<IChangingRecordKeyNumber>();

  const changeViewMode = (params?: IChangingRecordKeyNumber) => {
    setChildChangingMode(params);
  };

  const onClickVersionIcon = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    if (isVersionsOpen) {
      setChildChangingMode(undefined);
      setActiveOfferingGroupVersion(offeringGroup);
      setIsVersionsOpen(false);
    } else {
      offeringGroup?.id && loadOfferingGroupVersions(offeringGroup.id);
      setIsVersionsOpen(true);
    }
  };

  // Добавление при создании версии
  const enrichedOfferVersions =
    useMemo(() => {
      if (!childChangingMode) return offeringGroupsVersions;
      if (childChangingMode[1] === "add") {
        const [pos] = childChangingMode;
        const baseVersion = offeringGroupsVersions[pos];
        const firstPart = offeringGroupsVersions.slice(0, pos);
        const secondPart = offeringGroupsVersions.slice(pos);
        return [...firstPart, baseVersion, ...secondPart];
      }
      return offeringGroupsVersions;
    }, [offeringGroupsVersions, childChangingMode]) || [];

  const sumbitUpdateCloseDateOfferingGroupVersion = async (values: IOfferingGroupForm) => {
    dispatch(
      updateOfferingGroupCloseDate({
        offeringGroupId: Number(offeringGroupId),
        onDate: toHttpFormatEndOfDay(values?.toDate),
      })
    ).then(() => {
      setChildChangingMode(undefined);
    });
  };

  const sumbitUpdateOfferingGroupVersion = async (values: IOfferingGroupForm) => {
    const force = childChangingMode?.[1] === "edit";

    const params = {
      name: values?.name,
      groupingTypeId: values?.groupingTypeId,
    };

    dispatch(
      updateOfferingGroup({
        body: params,
        params: {
          offeringGroupId: Number(offeringGroupId),
          params: { force, fromDate: activeOfferingGroupVersion?.fromDate },
        },
      })
    ).then(() => {
      setChildChangingMode(undefined);
    });
  };

  const sumbitAddOfferingGroupVersion = async (values: IOfferingGroupForm) => {
    const force = childChangingMode?.[1] === "edit";

    const fromDate = toHttpFormatStartOfDay(offeringGroupParamsForm.getFieldValue("fromDate"));

    const params = {
      name: values?.name,
      groupingTypeId: values?.groupingTypeId,
      fromDate,
    };

    dispatch(
      updateOfferingGroup({
        body: params,
        params: {
          offeringGroupId: Number(offeringGroupId),
          params: { force, fromDate },
        },
      })
    ).then(() => {
      setChildChangingMode(undefined);
    });
  };

  const [mainTreeData, setMainTreeData] = useState<IOfferingGroupCreate>();

  const onFinish = (fields) => {
    if (childChangingMode) {
      const mode: IChangingMode = childChangingMode[1];
      switch (mode) {
        case "edit":
          sumbitUpdateOfferingGroupVersion(fields);
          break;
        case "add":
          sumbitAddOfferingGroupVersion(fields);
          break;
        case "close":
          sumbitUpdateCloseDateOfferingGroupVersion(fields);
          break;
      }
    }
  };

  return (
    <div className="offering-group-view">
      <Row className="offering-group-view-title" align="middle">
        <Link to="/offering-groups">
          <LeftOutlined style={{ fontSize: 16 }} />
        </Link>

        <h1>Просмотр группы</h1>

        <Divider className="offering-group-view-divider" style={{ marginBottom: 0 }} />
      </Row>

      <Form
        form={offeringGroupParamsForm}
        onFinish={onFinish}
        labelAlign="right"
        initialValues={{}}
        style={{ width: "100%" }}
      >
        <Collapse
          activeKey={isCollapseOpen ? ["1"] : []}
          bordered={false}
          onChange={toggleCollapse}
        >
          <Collapse.Panel
            key="1"
            header={
              <Row justify="space-between" className="offering-group-view-collapse-title">
                <h3>Группа</h3>
                {isCollapseOpen && (
                  <Row align="middle" gutter={7}>
                    <Col>
                      {isVersionsOpen && <VersionsShownIcon onClick={onClickVersionIcon} />}
                      {!isVersionsOpen && <VersionsHiddenIcon onClick={onClickVersionIcon} />}
                    </Col>
                    <Col>
                      <DotsIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          showHistoryModal();
                        }}
                      />
                    </Col>
                  </Row>
                )}
              </Row>
            }
          >
            <OfferingGroupParams
              offeringGroup={offeringGroup}
              mode="view"
              isLoading={checkIsLoading(offeringGroupLoading)}
            />

            {isVersionsOpen &&
              enrichedOfferVersions.map((version, idx) => (
                <div
                  key={idx}
                  style={
                    version.fromDate === activeOfferingGroupVersion?.fromDate
                      ? { border: "2px solid #4cf" }
                      : undefined
                  }
                  onClick={() => {
                    setActiveOfferingGroupVersion(version);
                  }}
                >
                  {childChangingMode?.[0] !== idx && (
                    <OfferingGroupParams
                      offeringGroup={version}
                      isChild
                      mode="view"
                      idx={idx}
                      isLatestVersion={idx === 0}
                      offeringGroupParamsForm={offeringGroupParamsForm}
                      isEditingDisabled={Boolean(childChangingMode)}
                      isLoading={checkIsLoading(offeringGroupsVersionsLoading)}
                      changeViewMode={changeViewMode}
                      setActiveOfferingGroupVersion={setActiveOfferingGroupVersion}
                    />
                  )}
                  {childChangingMode?.[0] === idx && (
                    <OfferingGroupParams
                      offeringGroup={version}
                      offeringGroupParamsForm={offeringGroupParamsForm}
                      childChangingMode={childChangingMode}
                      mode={childChangingMode[1] === "close" ? "edit_close_date" : "edit"}
                      isLoading={checkIsLoading(offeringGroupsVersionsLoading)}
                      onCancel={() => setChildChangingMode(undefined)}
                      onSubmit={offeringGroupParamsForm.submit}
                    />
                  )}
                </div>
              ))}
          </Collapse.Panel>
        </Collapse>

        <OfferingGroupTreeTable
          mode="view"
          offeringGroupParamsForm={offeringGroupParamsForm}
          offeringGroup={offeringGroup}
          mainTreeData={mainTreeData}
          setMainTreeData={setMainTreeData}
        />

        <Collapse
          activeKey={isCollapsePolicyOpen ? ["1"] : []}
          bordered={false}
          onChange={toggleCollapsePolicy}
        >
          <Collapse.Panel
            key="1"
            header={
              <Row justify="space-between" className="offering-group-view-collapse-title">
                <h3>Политики</h3>
              </Row>
            }
          >
            <OfferingGroupPolicyTable
              offeringGroupsPolicy={offeringGroupsPolicy}
              isLoading={checkIsLoading(offeringGroupsPolicyLoading)}
            />
          </Collapse.Panel>
        </Collapse>
      </Form>
      <HistoryAndPropertyModal
        objectTypeSysname="entity_group"
        objectId={objectId}
        handleHideModal={() => setHistoryModalVisible(false)}
        visibleModal={historyModalVisible}
      />
    </div>
  );
};

export default OfferingGroupView;
